/*
 * Size guide behaviour.
 *
 * - CM/IN and INT/EU/US/UK are non-destructive text swaps: every cell keeps all of
 *   its variants in data attributes, so switching back and forth never loses a value.
 * - Tabs only exist from the desktop breakpoint up. Below it the tab bar is hidden by
 *   CSS and every panel stays visible, so the guide reads as one continuous document.
 */
(function () {
  const ROOT_SELECTOR = '[data-size-guide]';
  const desktop = window.matchMedia('(min-width: 990px)');

  /* ---------------------------------------------------------------- units */

  function setPressed(buttons, activeValue, attribute) {
    buttons.forEach((button) => {
      const isActive = button.getAttribute(attribute) === activeValue;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function applyUnit(root, unit) {
    setPressed(root.querySelectorAll('[data-sg-unit-btn]'), unit, 'data-sg-unit-btn');

    root.querySelectorAll('.size-guide__cell[data-cm]').forEach((cell) => {
      const value = unit === 'in' ? cell.getAttribute('data-in') : cell.getAttribute('data-cm');
      if (value) cell.textContent = value;
    });
  }

  function applySystem(root, system) {
    setPressed(root.querySelectorAll('[data-sg-system-btn]'), system, 'data-sg-system-btn');

    root.querySelectorAll('[data-sg-size]').forEach((cell) => {
      const value = cell.getAttribute('data-size-' + system);
      cell.textContent = value || cell.getAttribute('data-size-int') || '';
    });
  }

  /* ----------------------------------------------------------------- tabs */

  function tabsOf(root) {
    return Array.from(root.querySelectorAll('[data-sg-tab]'));
  }

  function panelsOf(root) {
    return Array.from(root.querySelectorAll('[data-sg-panel]'));
  }

  function syncTabs(root) {
    const panels = panelsOf(root);
    const tabList = root.querySelector('[role="tablist"]');

    // Drop tabs whose panel was not rendered (e.g. a product with no measurements).
    const tabs = tabsOf(root).filter((tab) => {
      const name = tab.getAttribute('data-sg-tab');
      const orphan = !panels.some((panel) => panel.getAttribute('data-sg-panel') === name);
      tab.hidden = orphan;
      return !orphan;
    });

    // A single panel needs no tab bar.
    const tabbed = desktop.matches && tabs.length > 1;
    if (tabList) tabList.hidden = !tabbed;

    if (!tabbed) {
      panels.forEach((panel) => {
        panel.hidden = false;
      });
      return;
    }

    let active = root.getAttribute('data-sg-active-tab');
    if (!tabs.some((tab) => tab.getAttribute('data-sg-tab') === active)) {
      active = tabs[0].getAttribute('data-sg-tab');
      root.setAttribute('data-sg-active-tab', active);
    }

    tabs.forEach((tab) => {
      const isActive = tab.getAttribute('data-sg-tab') === active;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel) => {
      panel.hidden = panel.getAttribute('data-sg-panel') !== active;
    });
  }

  function selectTab(root, name, focus) {
    root.setAttribute('data-sg-active-tab', name);
    syncTabs(root);
    if (focus) {
      const tab = root.querySelector('[data-sg-tab="' + name + '"]');
      if (tab) tab.focus();
    }
  }

  function syncAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(syncTabs);
  }

  /* ------------------------------------------------------------- listeners */

  document.addEventListener('click', function (event) {
    const unitButton = event.target.closest('[data-sg-unit-btn]');
    if (unitButton) {
      const root = unitButton.closest(ROOT_SELECTOR);
      if (root) applyUnit(root, unitButton.getAttribute('data-sg-unit-btn'));
      return;
    }

    const systemButton = event.target.closest('[data-sg-system-btn]');
    if (systemButton) {
      const root = systemButton.closest(ROOT_SELECTOR);
      if (root) applySystem(root, systemButton.getAttribute('data-sg-system-btn'));
      return;
    }

    const tabButton = event.target.closest('[data-sg-tab]');
    if (tabButton) {
      const root = tabButton.closest(ROOT_SELECTOR);
      if (root) selectTab(root, tabButton.getAttribute('data-sg-tab'), false);
    }
  });

  document.addEventListener('keydown', function (event) {
    const tabButton = event.target.closest && event.target.closest('[data-sg-tab]');
    if (!tabButton) return;

    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;

    const root = tabButton.closest(ROOT_SELECTOR);
    if (!root) return;

    const tabs = tabsOf(root).filter((tab) => !tab.hidden);
    const next = tabs[(tabs.indexOf(tabButton) + step + tabs.length) % tabs.length];
    if (!next) return;

    event.preventDefault();
    selectTab(root, next.getAttribute('data-sg-tab'), true);
  });

  // A measurement illustration that was never uploaded should leave no gap.
  document.addEventListener(
    'error',
    function (event) {
      const image = event.target;
      if (!image || !image.classList || !image.classList.contains('size-guide__measure-image')) return;
      const figure = image.closest('.size-guide__measure-figure');
      if (figure) figure.hidden = true;
    },
    true
  );

  desktop.addEventListener('change', syncAll);
  document.addEventListener('shopify:section:load', syncAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }
})();
